import { useSymbolsService } from 'symbols/hooks/useSymbolsService';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { useEffect, useState } from 'react';
import { mapSymbol } from 'symbols/indexMappings/indexMappingsUtils';
import { toFillsGraphStreamedDataUid } from './FillsGraphStreamedDataHelpers';
import { useSelector } from 'react-redux';
import { selectFillsGraphStreamRequestBySymbolStreamedData } from 'widgets/fillsGraph/store/streamedData/streamSlice';
import { useSelectIndexMappingsUserPreference } from 'symbols/indexMappings/hooks';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';

export function useFillsGraphStreamedData(
    symbol: string | undefined,
    widgetConfigIndexFallback: string
) {
    const service = useSymbolsService();
    const { symbology } = useSelectAppSettings();
    const [streamKey, setStreamKey] = useState<string>('');
    const [
        widgetConfigIndexFallbackInstinet,
        setWidgetConfigIndexFallbackInstinet,
    ] = useState(widgetConfigIndexFallback);

    const userPrefsMappings = useSelectIndexMappingsUserPreference();
    const fillsGraphStreamedData = useSelector(
        selectFillsGraphStreamRequestBySymbolStreamedData
    );

    useEffect(() => {
        if (widgetConfigIndexFallback === 'DEFAULT') {
            setWidgetConfigIndexFallbackInstinet(widgetConfigIndexFallback);
            return;
        }
        if (symbology !== SymbologyValues.Instinet) {
            service
                .convertSymbol(
                    widgetConfigIndexFallback,
                    symbology,
                    SymbologyValues.Instinet
                )
                .then((converted) => {
                    if (converted) {
                        setWidgetConfigIndexFallbackInstinet(converted);
                    }
                });
        }
    }, [widgetConfigIndexFallback, symbology, service]);

    useEffect(() => {
        if (!symbol) {
            return;
        }

        if (widgetConfigIndexFallback === 'DEFAULT') {
            setStreamKey(toFillsGraphStreamedDataUid(symbol, 'DEFAULT'));
            return;
        }

        service.getSymbolInfo(symbol, symbology).then((symbolInfo) => {
            if (!symbolInfo) {
                return;
            }
            const { index } = mapSymbol(
                widgetConfigIndexFallbackInstinet,
                userPrefsMappings ?? [],
                symbolInfo,
                1
            );

            setStreamKey(toFillsGraphStreamedDataUid(symbol, index));
        });
    }, [
        symbol,
        symbology,
        service,
        userPrefsMappings,
        widgetConfigIndexFallbackInstinet,
        widgetConfigIndexFallback,
    ]);

    const streamedData = streamKey
        ? fillsGraphStreamedData[streamKey]
        : undefined;

    return { streamedData, streamKey };
}
